import { ReactNode, forwardRef } from 'react';

import { type ClassValue, cn, tv } from '@eluve/components';

const floatingActionButtonStyles = tv({
  base: 'relative flex h-8 min-w-8 shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded-full p-1 transition-[color,background-color,box-shadow,opacity] duration-150',
  variants: {
    disabled: { true: 'cursor-not-allowed opacity-50' },
    extended: {
      true: "after:absolute after:-inset-2 after:rounded-full after:content-[''] not-first:after:-left-1 not-last:after:-right-1",
    },
    variant: {
      primary: 'ring-1 ring-inset',
      secondary: 'ring-1 ring-inset',
      ghost: 'bg-transparent ring-inset hover:ring-1',
    },
    tone: {
      dark: 'text-white ring-white/5',
      light: 'text-text-primary ring-black/4',
    },
  },
  compoundVariants: [
    {
      tone: 'dark',
      variant: 'primary',
      className: 'bg-white/10 hover:bg-white/15 active:bg-white/5',
    },
    {
      tone: 'dark',
      variant: 'secondary',
      className: 'bg-white/5 hover:bg-white/15 active:bg-white/10',
    },
    {
      tone: 'dark',
      variant: 'ghost',
      className: 'hover:bg-white/15 active:bg-white/10',
    },
    {
      tone: 'light',
      variant: 'primary',
      className: 'bg-black/6 hover:bg-black/8 active:bg-black/4',
    },
    {
      tone: 'light',
      variant: 'secondary',
      className: 'bg-black/4 hover:bg-black/8 active:bg-black/6',
    },
    {
      tone: 'light',
      variant: 'ghost',
      className: 'hover:bg-black/8 active:bg-black/6',
    },
    { disabled: true, tone: 'dark', className: 'active:bg-white/15!' },
    { disabled: true, tone: 'light', className: 'active:bg-black/8!' },
  ],
  defaultVariants: {
    disabled: false,
    extended: true,
    variant: 'ghost',
    tone: 'dark',
  },
});

export type FloatingActionButtonVariant =
  keyof typeof floatingActionButtonStyles.variants.variant;

export type FloatingActionButtonTone =
  keyof typeof floatingActionButtonStyles.variants.tone;

type FloatingActionButtonProps = {
  children?: ReactNode;
  className?: ClassValue;
  disabled?: boolean;
  /** Whether the button's click area is extended. */
  extended?: boolean;
  onClick?: () => void;
  /**
   * Fires when the user presses the button while disabled (e.g. blocked-
   * interaction analytics). Wired to pointer-down: native `disabled`
   * suppresses click events, but browsers still dispatch pointer events
   * on disabled controls (verified Chromium/Firefox/WebKit).
   */
  onClickDisabled?: () => void;
  variant?: FloatingActionButtonVariant;
  tone?: FloatingActionButtonTone;
};

export const FloatingActionButton = forwardRef<
  HTMLButtonElement,
  FloatingActionButtonProps
>(
  (
    {
      children,
      onClick,
      onClickDisabled,
      className,
      extended,
      disabled = false,
      variant = 'ghost',
      tone = 'dark',
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        floatingActionButtonStyles({ disabled, extended, variant, tone }),
        className,
      )}
      onClick={disabled ? undefined : onClick}
      onPointerDown={disabled ? onClickDisabled : undefined}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  ),
);
